/**
 * The KPIs each dashboard offers in the Leadership Brief before anyone picks
 * (`atlas.executive.config.starting_sets.<dashboard>`), and a person's starting
 * "My KPIs" list.
 *
 * Measures are only ever picked from the project's own GUS catalogue
 * (`GET /api/atlas-v2/executive/catalogue`, fetched as the signed-in admin):
 * an id is never typed. Each stored item is checked against that catalogue
 * by GUS's own rule, and one the project cannot serve is shown, not hidden.
 * Without the catalogue nothing can be added, but a list can still be
 * reordered, trimmed or handed back to GUS's default.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExecutiveAnswer, ExecutiveCatalogue, executiveAPI } from '../../services/api';
import { Bilingual, SettingError } from '../satisfaction/satisfactionSettings';
import {
  CatalogueMeasure, DASHBOARD_LABELS, EXECUTIVE_CONFIG, MAX_KPIS, MY_KPIS, RANK, STARTING_SET_KEYS, StartingItem,
  catalogueIndex, measureLabel, readItem, servability, writeItem,
} from './executiveSettings';
import {
  Arabic, FieldLabel, Help, IconButton, Item, ItemList, ItemText, Key, Note, Row, Select, SmallButton, State,
} from './styles';

interface Props {
  projectId?: string;
  /** The stored `config.starting_sets`, whatever its shape. */
  sets: unknown;
  /** The refused values under `config.starting_sets`. */
  errors: SettingError[];
  /** Write one dashboard's list; undefined hands it back to GUS's default. */
  onSet: (dashboard: string, list: unknown[] | undefined) => void;
  /** Remove a key GUS ignores. */
  onRemoveKey: (key: string) => void;
  disabled?: boolean;
}

const SETS = `${EXECUTIVE_CONFIG}.starting_sets`;
const isPlain = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const isCatalogue = (v: unknown): v is ExecutiveCatalogue => isPlain(v) && Array.isArray(v.groups)
  && Array.isArray(v.levels) && Array.isArray(v.domain_off)
  && v.groups.every((g: unknown) => isPlain(g) && Array.isArray(g.measures));

const DEFAULT_TEXT: Record<string, Bilingual> = {
  [MY_KPIS]: {
    en: 'Not set: GUS starts each person with the first 2 measures of every dashboard’s set, at most 8.',
    ar: 'غير مضبوطة: يبدأ GUS لكل شخص بأول مقياسين من قائمة كل لوحة معلومات، 8 على الأكثر.',
  },
};
const DASHBOARD_DEFAULT: Bilingual = {
  en: 'Not set: GUS offers its own short list, up to 4, from the measures this dashboard can read for the whole place.',
  ar: 'غير مضبوطة: يعرض GUS قائمته القصيرة، حتى 4 مقاييس، من المقاييس التي تقرؤها لوحة المعلومات للمكان كله.',
};

type Kind = 'whole' | 'rank:high' | 'rank:low';

const Both: React.FC<Bilingual> = ({ en, ar }) => (
  <>
    {en} <span lang="ar" dir="rtl">/ {ar}</span>
  </>
);

const StartingSetsEditor: React.FC<Props> = ({ projectId, sets, errors, onSet, onRemoveKey, disabled = false }) => {
  const [dashboard, setDashboard] = useState<string>(STARTING_SET_KEYS[0]);
  const [answer, setAnswer] = useState<ExecutiveAnswer<ExecutiveCatalogue> | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const [kind, setKind] = useState<Kind>('whole');
  const [level, setLevel] = useState('');
  const [note, setNote] = useState<Bilingual | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setAnswer(await executiveAPI.catalogue(projectId));
    } catch (e: any) {
      setLoadError(e?.response?.data?.detail || e?.message || 'Failed to ask the portal for the catalogue');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // The backend already refuses a catalogue in a shape it does not know; this guards the page against one anyway.
  const catalogue = answer?.outcome === 'ok' && isCatalogue(answer.body) ? answer.body : null;
  const index = useMemo(() => (catalogue ? catalogueIndex(catalogue) : new Map<string, CatalogueMeasure>()), [catalogue]);
  const servedKey = (x: StartingItem) => (RANK.test(x.measure)
    ? `${x.measure}\u0000${x.level ?? catalogue?.coarsest ?? ''}`
    : x.measure);
  const levelLabel = (code: string | null) => {
    if (!code) return null;
    const l = catalogue?.levels.find((x) => x.code === code);
    return { en: l?.label.en || code, ar: l?.label.ar || code };
  };

  const stored: Record<string, unknown> = isPlain(sets) ? sets : {};
  const raw = stored[dashboard];
  const list: unknown[] | null = Array.isArray(raw) ? raw : null;
  const unknownKeys = Object.keys(stored).filter((k) => !STARTING_SET_KEYS.includes(k));
  const errorsHere = errors.filter((e) => e.path === `${SETS}.${dashboard}` || e.path.startsWith(`${SETS}.${dashboard}.`));
  const listErrors = errorsHere.filter((e) => e.path === `${SETS}.${dashboard}`);
  const itemErrors = (i: number) => errorsHere.filter((e) => e.path === `${SETS}.${dashboard}.${i}`
    || e.path.startsWith(`${SETS}.${dashboard}.${i}.`));

  // What the picker offers: this dashboard's measures (every dashboard's for My KPIs) that GUS can serve one way or
  // another. A measure it can serve neither for the whole place nor ranked is left out, and counted.
  const groups = (catalogue?.groups ?? []).filter((g) => dashboard === MY_KPIS || g.dashboard === dashboard);
  const offered = groups.map((g) => ({
    group: g,
    measures: g.measures.filter((m) => m.city.ok || m.rank_levels.length > 0),
  })).filter((g) => g.measures.length > 0);
  const notOffered = groups.reduce((n, g) => n + g.measures.filter((m) => !m.city.ok && !m.rank_levels.length).length, 0);
  const picked = index.get(pick);

  const choose = (id: string) => {
    setPick(id);
    setNote(null);
    const m = index.get(id);
    if (!m) return;
    const nextKind: Kind = m.city.ok ? 'whole' : 'rank:high';
    setKind(nextKind);
    const coarsest = catalogue?.coarsest ?? null;
    setLevel(coarsest && m.rank_levels.includes(coarsest) ? coarsest : m.rank_levels[0] ?? '');
  };

  const add = () => {
    if (!picked) return;
    const item: StartingItem = kind === 'whole'
      ? { measure: picked.id, level: null }
      : { measure: `${kind}:${picked.id}`, level: level || null };
    const current = list ?? [];
    if (current.length >= MAX_KPIS) {
      setNote({ en: `A list holds at most ${MAX_KPIS} measures.`, ar: `تتسع القائمة لـ ${MAX_KPIS} مقياسًا على الأكثر.` });
      return;
    }
    const same = current.map(readItem).some((x) => x && servedKey(x) === servedKey(item));
    if (same) {
      setNote({ en: 'That measure is already in the list.', ar: 'هذا المقياس موجود في القائمة بالفعل.' });
      return;
    }
    onSet(dashboard, [...current, writeItem(item)]);
    setPick('');
    setNote(null);
  };

  const move = (i: number, by: -1 | 1) => {
    if (!list) return;
    const next = [...list];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    onSet(dashboard, next);
  };
  // The last item out hands the list back to GUS's default: an empty list would mean the same, and is refused.
  const remove = (i: number) => {
    if (!list) return;
    const next = list.filter((_, j) => j !== i);
    onSet(dashboard, next.length ? next : undefined);
  };

  // Which items GUS would keep, by its own key: (measure, the level it serves). A rank item with no level ranks at
  // the coarsest, so it is the same item as one that names the coarsest level, and GUS keeps only the first.
  const servedKeys = (list ?? []).map((raw) => {
    const x = readItem(raw);
    return x ? servedKey(x) : null;
  });

  const describe = (item: StartingItem) => {
    const rank = RANK.exec(item.measure);
    const base = rank ? rank[2] : item.measure;
    const m = index.get(base);
    const en = measureLabel(m, 'en') ?? base;
    const ar = measureLabel(m, 'ar');
    if (!rank) return { en, ar };
    const lv = levelLabel(item.level ?? catalogue?.coarsest ?? null);
    const high = rank[1] === 'high';
    return {
      en: `${high ? 'Highest' : 'Lowest'} places by ${en}${lv ? `, by ${lv.en}` : ''}${item.level ? '' : ' (coarsest level)'}`,
      ar: ar ? `${high ? 'أعلى' : 'أدنى'} الأماكن حسب ${ar}${lv ? `، حسب ${lv.ar}` : ''}` : null,
    };
  };

  return (
    <>
      <Row>
        <FieldLabel>
          <span>Dashboard <Arabic lang="ar" dir="rtl">/ لوحة المعلومات</Arabic></span>
          <Select value={dashboard} onChange={(e) => { setDashboard(e.target.value); setPick(''); setNote(null); }}>
            {STARTING_SET_KEYS.map((k) => (
              <option key={k} value={k}>
                {DASHBOARD_LABELS[k].en} / {DASHBOARD_LABELS[k].ar}
                {Array.isArray(stored[k]) ? ' (set / مضبوطة)' : ''}
              </option>
            ))}
          </Select>
        </FieldLabel>
        {list && (
          <SmallButton type="button" disabled={disabled} onClick={() => onSet(dashboard, undefined)}>
            Use GUS default / استخدم الإعداد الافتراضي في GUS
          </SmallButton>
        )}
      </Row>

      {unknownKeys.map((k) => (
        <Note key={k} $tone="error" role="alert">
          <Key>{k}</Key>: <Both
            en="GUS knows no such dashboard and ignores this list."
            ar="لا يعرف GUS لوحة معلومات بهذا الاسم ويتجاهل هذه القائمة."
          />{' '}
          <IconButton type="button" disabled={disabled} onClick={() => onRemoveKey(k)}>
            Remove / احذف
          </IconButton>
        </Note>
      ))}

      {raw === undefined || raw === null ? (
        <State><Both {...(DEFAULT_TEXT[dashboard] ?? DASHBOARD_DEFAULT)} /></State>
      ) : (
        listErrors.map((e) => (
          <Note key={e.rule} $tone="error" role="alert"><Both {...e.message} /></Note>
        ))
      )}

      {list && list.length > 0 && (
        <ItemList>
          {list.map((rawItem, i) => {
            const item = readItem(rawItem);
            const problems = itemErrors(i);
            const serving = item && catalogue ? servability(item, catalogue, index) : null;
            const repeated = servedKeys[i] !== null && servedKeys.indexOf(servedKeys[i]) < i;
            const tone = problems.length ? 'error' : (serving && serving.state !== 'ok') || repeated ? 'warning' : 'ok';
            const text = item ? describe(item) : null;
            return (
              // The position is the identity: the same measure can sit at two levels, and the list reorders.
              <Item key={i} $tone={tone}>
                <ItemText>
                  <span>{text ? text.en : JSON.stringify(rawItem)}</span>
                  {text?.ar && <Arabic lang="ar" dir="rtl">{text.ar}</Arabic>}
                  {item && <Key>{item.measure}{item.level ? ` @ ${item.level}` : ''}</Key>}
                  {problems.map((e) => (
                    <Note key={e.rule} $tone="error" role="alert"><Both {...e.message} /></Note>
                  ))}
                  {serving && serving.state !== 'ok' && (
                    <Note $tone="warning"><Both {...serving.why} /></Note>
                  )}
                  {repeated && !problems.length && (
                    <Note $tone="warning">
                      <Both
                        en="The same as an item above once its level is filled in (the coarsest). GUS keeps only the first."
                        ar="هو نفسه عنصر أعلاه بعد إكمال مستواه (الأكثر شمولًا). يُبقي GUS الأول فقط."
                      />
                    </Note>
                  )}
                </ItemText>
                <IconButton type="button" aria-label="Move up" disabled={disabled || i === 0} onClick={() => move(i, -1)}>↑</IconButton>
                <IconButton type="button" aria-label="Move down" disabled={disabled || i === list.length - 1} onClick={() => move(i, 1)}>↓</IconButton>
                <IconButton type="button" aria-label="Remove" disabled={disabled} onClick={() => remove(i)}>✕</IconButton>
              </Item>
            );
          })}
        </ItemList>
      )}

      {!projectId ? (
        <Help>
          <Both
            en="Save the project first: measures are picked from its own GUS catalogue."
            ar="احفظ المشروع أولًا: تُختار المقاييس من كتالوج GUS الخاص به."
          />
        </Help>
      ) : loading ? (
        <State><Both en="Reading this project’s catalogue…" ar="جارٍ قراءة كتالوج هذا المشروع…" /></State>
      ) : !catalogue ? (
        <>
          <Note $tone="warning">
            {loadError ?? <Both en={answer?.message ?? ''} ar={answer?.message_ar ?? ''} />}
            {answer?.detail && <> ({answer.detail})</>}
          </Note>
          <Help>
            <Both
              en="Without the catalogue no measure can be added, and the list above is not checked. It can still be reordered or cleared."
              ar="من دون الكتالوج لا يمكن إضافة أي مقياس، ولا يُتحقق من القائمة أعلاه. لكن يمكن إعادة ترتيبها أو مسحها."
            />
          </Help>
          <Row>
            <SmallButton type="button" onClick={load}>Try again / أعد المحاولة</SmallButton>
          </Row>
        </>
      ) : (
        <>
          {catalogue.partial && (
            <Note $tone="warning">
              <Both
                en="GUS sent only part of its catalogue, so a measure missing from it is not proof the project cannot serve it."
                ar="أرسل GUS جزءًا من كتالوجه فقط، لذا فإن غياب مقياس عنه لا يثبت أن المشروع لا يستطيع تقديمه."
              />
            </Note>
          )}
          <Row>
            <FieldLabel>
              <span>Add a measure <Arabic lang="ar" dir="rtl">/ أضف مقياسًا</Arabic></span>
              <Select value={pick} disabled={disabled} onChange={(e) => choose(e.target.value)}>
                <option value="">Choose… / اختر…</option>
                {offered.map(({ group, measures }) => (
                  <optgroup key={group.id} label={`${group.label.en ?? group.id}${group.label.ar ? ` / ${group.label.ar}` : ''}`}>
                    {measures.map((m) => (
                      <option key={m.id} value={m.id}>
                        {measureLabel(m, 'en') ?? m.id}
                        {measureLabel(m, 'ar') ? ` / ${measureLabel(m, 'ar')}` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </FieldLabel>
            {picked && (
              <FieldLabel>
                <span>Read as <Arabic lang="ar" dir="rtl">/ يُقرأ بوصفه</Arabic></span>
                <Select value={kind} disabled={disabled} onChange={(e) => setKind(e.target.value as Kind)}>
                  <option value="whole" disabled={!picked.city.ok}>Whole place / المكان كله</option>
                  <option value="rank:high" disabled={!picked.rank_levels.length}>Highest places / الأماكن الأعلى</option>
                  <option value="rank:low" disabled={!picked.rank_levels.length}>Lowest places / الأماكن الأدنى</option>
                </Select>
              </FieldLabel>
            )}
            {picked && kind !== 'whole' && (
              <FieldLabel>
                <span>Ranked across <Arabic lang="ar" dir="rtl">/ مرتّبة على مستوى</Arabic></span>
                <Select value={level} disabled={disabled} onChange={(e) => setLevel(e.target.value)}>
                  {picked.rank_levels.map((code) => {
                    const l = levelLabel(code);
                    return <option key={code} value={code}>{l?.en} / {l?.ar}</option>;
                  })}
                </Select>
              </FieldLabel>
            )}
            <SmallButton type="button" disabled={disabled || !picked} onClick={add}>Add / أضف</SmallButton>
          </Row>
          {note && <Note $tone="warning" role="status"><Both {...note} /></Note>}
          {notOffered > 0 && (
            <Help>
              <Both
                en={`${notOffered} measure${notOffered === 1 ? '' : 's'} this project cannot serve ${notOffered === 1 ? 'is' : 'are'} not offered.`}
                ar={`لا تُعرض المقاييس التي لا يستطيع هذا المشروع تقديمها (${notOffered}).`}
              />
            </Help>
          )}
        </>
      )}
    </>
  );
};

export default StartingSetsEditor;
